import type { Call } from '@/types/validationSchemas/CallSchema';

export const ACTIVE_CUSTOMER_CALL_STATUSES = new Set([
  'initiated',
  'ringing',
  'in-progress',
  'answered',
]);

type CustomerCallTarget =
  | string
  | {
      _id?: unknown;
      phone?: string | null;
    }
  | null
  | undefined;

const normalizeId = (value: unknown): string => {
  if (!value) return '';
  if (typeof value === 'object' && '_id' in value) {
    return String((value as { _id?: unknown })._id || '');
  }
  return String(value);
};

const normalizePhone = (value: unknown): string =>
  typeof value === 'string' ? value.replace(/\D/g, '') : '';

export const findActiveCustomerCall = (
  calls: Call[] | null | undefined,
  customer: CustomerCallTarget,
): Call | undefined => {
  const customerId = normalizeId(customer);
  const customerPhone =
    typeof customer === 'object' && customer ? normalizePhone(customer.phone) : '';

  if (!customerId && !customerPhone) return undefined;

  return (calls || []).find((call) => {
    if (!ACTIVE_CUSTOMER_CALL_STATUSES.has(String(call.callStatus || '').toLowerCase())) {
      return false;
    }

    const callCustomerId = normalizeId(call._customer);
    if (customerId && callCustomerId && customerId === callCustomerId) {
      return true;
    }

    if (!customerPhone) return false;
    const populatedCustomerPhone =
      typeof call._customer === 'object' && call._customer
        ? normalizePhone(call._customer.phone)
        : '';
    return [call.to, call.from, populatedCustomerPhone].some(
      (phone) => normalizePhone(phone) === customerPhone,
    );
  });
};

export const isCustomerOnActiveCall = (
  calls: Call[] | null | undefined,
  customer: CustomerCallTarget,
): boolean => Boolean(findActiveCustomerCall(calls, customer));
